/**
 * Tipos de elementos formatados
 */
export type FormattedElement = {
  type: 'text' | 'bold' | 'bullet' | 'hashtag' | 'empty-line';
  content: string;
  indent?: number;
};

export type FormattedLine = {
  elements: FormattedElement[];
  type: 'normal' | 'bullet' | 'empty';
};

/**
 * Parser de formatação Markdown simples para posts
 *
 * Suporta:
 * - **texto** -> Negrito
 * - • texto ou - texto -> Bullet point
 * - #hashtag -> Hashtag colorida
 * - Quebras de linha reais
 * - Linhas vazias para espaçamento
 */
export class TextFormatter {
  /**
   * Parse o texto completo e retorna linhas formatadas
   */
  static parse(text: string): FormattedLine[] {
    const lines = text.split('\n');
    const formattedLines: FormattedLine[] = [];

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Linha vazia
      if (trimmedLine === '') {
        formattedLines.push({
          type: 'empty',
          elements: [{ type: 'empty-line', content: '' }],
        });
        continue;
      }

      // Detectar bullet point
      const bulletMatch = trimmedLine.match(/^[•-]\s+(.+)$/);
      if (bulletMatch) {
        const bulletContent = bulletMatch[1];
        const elements = this.parseInlineFormatting(bulletContent);
        formattedLines.push({
          type: 'bullet',
          elements: [{ type: 'bullet', content: '•', indent: 0 }, ...elements],
        });
        continue;
      }

      // Linha normal com formatação inline
      const elements = this.parseInlineFormatting(trimmedLine);
      formattedLines.push({
        type: 'normal',
        elements,
      });
    }

    return formattedLines;
  }

  /**
   * Parse formatação inline (negrito, hashtags) dentro de uma linha
   */
  private static parseInlineFormatting(text: string): FormattedElement[] {
    const elements: FormattedElement[] = [];
    let currentIndex = 0;

    // Regex para encontrar **texto**, #hashtag
    const boldRegex = /\*\*([^*]+)\*\*/g;
    const hashtagRegex = /#\w+/g;

    // Combinar todos os matches e ordenar por posição
    const matches: Array<{ start: number; end: number; type: 'bold' | 'hashtag'; content: string }> = [];

    // Encontrar negritos
    let boldMatch;
    while ((boldMatch = boldRegex.exec(text)) !== null) {
      matches.push({
        start: boldMatch.index,
        end: boldMatch.index + boldMatch[0].length,
        type: 'bold',
        content: boldMatch[1], // Conteúdo sem os **
      });
    }

    // Encontrar hashtags
    let hashMatch;
    while ((hashMatch = hashtagRegex.exec(text)) !== null) {
      matches.push({
        start: hashMatch.index,
        end: hashMatch.index + hashMatch[0].length,
        type: 'hashtag',
        content: hashMatch[0],
      });
    }

    // Ordenar por posição
    matches.sort((a, b) => a.start - b.start);

    // Processar matches
    for (const match of matches) {
      // Adicionar texto antes do match
      if (currentIndex < match.start) {
        const plainText = text.substring(currentIndex, match.start);
        if (plainText) {
          elements.push({ type: 'text', content: plainText });
        }
      }

      elements.push({
        type: match.type,
        content: match.content,
      });

      currentIndex = match.end;
    }

    // Adicionar texto restante
    if (currentIndex < text.length) {
      const remainingText = text.substring(currentIndex);
      if (remainingText) {
        elements.push({ type: 'text', content: remainingText });
      }
    }

    // Se não encontrou nada, retornar texto plain
    if (elements.length === 0) {
      elements.push({ type: 'text', content: text });
    }

    return elements;
  }

  /**
   * Calcula a altura necessária para renderizar o texto formatado
   */
  static calculateHeight(
    lines: FormattedLine[],
    ctx: CanvasRenderingContext2D,
    fontSize: number,
    lineHeight: number,
    maxWidth: number
  ): number {
    let totalHeight = 0;

    for (const line of lines) {
      if (line.type === 'empty') {
        totalHeight += lineHeight * 0.5; // Espaçamento menor para linhas vazias
        continue;
      }

      // Para bullets e linhas normais, calcular wrap
      const wrappedLines = this.wrapLine(line, ctx, maxWidth, fontSize);
      totalHeight += wrappedLines * lineHeight;
    }

    return totalHeight;
  }

  /**
   * Calcula quantas linhas uma linha formatada vai ocupar (com wrap)
   */
  private static wrapLine(
    line: FormattedLine,
    ctx: CanvasRenderingContext2D,
    maxWidth: number,
    fontSize: number
  ): number {
    // Para bullets, reduzir maxWidth para dar espaço ao bullet
    const effectiveMaxWidth = line.type === 'bullet' ? maxWidth - 30 : maxWidth;

    let currentWidth = 0;
    let lineCount = 1;

    for (const element of line.elements) {
      if (element.type === 'bullet') {
        ctx.font = `${fontSize}px system-ui, -apple-system, sans-serif`;
        currentWidth += ctx.measureText('• ').width;
        continue;
      }

      // Configurar fonte baseado no tipo
      if (element.type === 'bold') {
        ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
      } else {
        ctx.font = `${fontSize}px system-ui, -apple-system, sans-serif`;
      }

      // Medir palavras e fazer wrap
      const words = element.content.split(' ');
      for (const word of words) {
        const wordWidth = ctx.measureText(word + ' ').width;

        if (currentWidth + wordWidth > effectiveMaxWidth && currentWidth > 0) {
          lineCount++;
          currentWidth = wordWidth;
        } else {
          currentWidth += wordWidth;
        }
      }
    }

    return lineCount;
  }
}
